import React, { useState } from 'react';
// The original code had imports for styles and theme, which caused a compilation error.
// We are now defining all the necessary styles directly within this component.

interface StallPaymentReviewProps {
  formData: StallFormData;
  onPrevious: () => void;
  loading: boolean;
  subscriptionAmount: number;
  subscriptionDurationMonths: number;
}

interface StallFormData {
  stallNumber: string;
  businessName: string;
  email: string;
  businessPhone: string;
  selectedVillage: string;
  selectedParish: string;
  selectedSubcounty: string;
  selectedDistrict: string;
  selectedCountry: string;
}

// 🔹 Inline styles to replace the imported styles
const styles: { [key: string]: React.CSSProperties } = {
  formContainer: {
    padding: '2rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    maxWidth: '600px',
    margin: '2rem auto',
  },
  subscriptionCard: {
    padding: '1.5rem',
    borderRadius: '8px',
    border: '1px solid #e9ecef',
    backgroundColor: '#ffffff',
  },
  sectionHeading: {
    color: '#343a40',
    marginBottom: '1rem',
  },
  detailCard: {
    marginBottom: '20px',
    border: '1px solid #ced4da',
    padding: '15px',
    borderRadius: '8px',
    backgroundColor: '#f1f3f5',
  },
  detailText: {
    margin: '5px 0',
    color: '#495057',
  },
  highlightText: {
    fontWeight: 'bold',
    color: '#667eea', // Replaced theme.colors.primary with a direct color
  },
  formRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '10px',
    marginTop: '20px',
  },
  button: {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    flex: 1,
  },
  previousButton: {
    backgroundColor: '#adb5bd',
    color: '#fff',
  },
  payButton: {
    backgroundColor: '#667eea', // Replaced theme.colors.primary with a direct color
    color: '#fff',
  },
  disabledButton: {
    backgroundColor: '#ced4da',
    cursor: 'not-allowed',
  },
};

const StallPaymentReview: React.FC<StallPaymentReviewProps> = ({
  formData,
  onPrevious,
  loading,
  subscriptionAmount,
  subscriptionDurationMonths,
}) => {
  const [processing, setProcessing] = useState(false);

  // Your Firebase function URL (replace with your actual function URL)
  const FIREBASE_FUNCTION_URL = "https://us-central1-grema-62003.cloudfunctions.net/pesapal_payment";
  
  // Build callback URL with stall number
  // Pesapal will append OrderTrackingId and OrderMerchantReference to this URL
  const callbackUrl = `${window.location.origin}/payment-callback?stallNumber=${(formData.stallNumber)}`;
  
  console.log("=== Payment Initiation ===");
  console.log("Stall Number:", formData.stallNumber);
  console.log("Callback URL being sent to Pesapal:", callbackUrl);
  
  // Handle Pay Now
  const handlePayNow = async () => {
    setProcessing(true);
    console.log("Starting Pesapal payment process...");

    try {
      // 🔹 Construct the payment payload - use stall number as merchant reference
      const paymentPayload = {
        id: formData.stallNumber, // Use stall number as merchant reference for easy tracking
        currency: "UGX",
        amount: subscriptionAmount,
        description: `Stall Subscription for ${formData.businessName}`,
        callback_url: callbackUrl,
        redirect_mode: "", // Leave this empty for default behavior
        notification_id: "c2f63263-791f-4742-87de-db6d5629e7ea", // From your Pesapal dashboard
        branch: "Main Branch", // Placeholder value
        billing_address: {
          email_address: formData.email,
          phone_number: formData.businessPhone,
          country_code: "UG",
          first_name: formData.businessName,
          middle_name: "",
          last_name: "",
          line_1: `${formData.selectedVillage}, ${formData.selectedParish}`,
          line_2: `${formData.selectedSubcounty}, ${formData.selectedDistrict}`,
          city: formData.selectedDistrict,
          state: "",
          postal_code: "",
          zip_code: "",
        },
      };

      console.log("=== Payment Initiation ===");
      console.log("Stall Number (Merchant Reference):", formData.stallNumber);
      console.log("Pesapal Payload:", JSON.stringify(paymentPayload, null, 2));

      // 🔹 Call Firebase function via fetch
      const response = await fetch(FIREBASE_FUNCTION_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(paymentPayload),
      });

      console.log("Firebase Function Response Status:", response.status);

      if (response.ok) {
        const result = await response.json();
        console.log("=== Payment Response ===");
        console.log("Pesapal Response:", JSON.stringify(result, null, 2));
        console.log("Order Tracking ID:", result.order_tracking_id);
        console.log("Merchant Reference:", result.merchant_reference);

        if (result.payment_url) {
          console.log("Redirecting to Pesapal:", result.payment_url);
          window.location.href = result.payment_url; // 🔹 Redirect user
        } else {
          // This case should be handled by the Firebase function
          console.error("No payment URL returned from Firebase function.");
          alert("Payment initialization failed: No redirect URL received.");
        }
      } else {
        const errorText = await response.text();
        console.error("Function call failed:", errorText);
        throw new Error(errorText);
      }
    } catch (error: any) {
      console.error("Payment initialization error:", error);
      alert(`Payment initialization failed: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div style={styles.formContainer}>
      <div style={styles.subscriptionCard}>
        <h3 style={styles.sectionHeading}>3. Review & Payment</h3>
        <div style={styles.detailCard}>
          <h4>Your Stall Details:</h4>
          <p style={styles.detailText}><strong>Stall Number:</strong> {formData.stallNumber}</p>
          <p style={styles.detailText}><strong>Business Name:</strong> {formData.businessName}</p>
          <p style={styles.detailText}><strong>Contact Email:</strong> {formData.email}</p>
          <p style={styles.detailText}><strong>Business Phone:</strong> {formData.businessPhone}</p>
          <p style={styles.detailText}><strong>Location:</strong> {formData.selectedVillage}, {formData.selectedParish}, {formData.selectedSubcounty}, {formData.selectedDistrict}: {formData.selectedCountry}</p>
        </div>
        <h4 style={styles.sectionHeading}>Subscription Details</h4>
        <p style={styles.detailText}>
          Cost:{' '}
          <span style={styles.highlightText}>
            UGX {subscriptionAmount.toLocaleString()}
          </span>
        </p>
        <p style={styles.detailText}>
          Your stall will be active for {subscriptionDurationMonths} month(s) after successful payment.
        </p>
        <div style={styles.formRow}>
          <button style={{ ...styles.button, ...styles.previousButton }} type="button" onClick={onPrevious} disabled={processing}>
            Previous
          </button>
          <button
            type="button"
            onClick={handlePayNow}
            disabled={processing || loading}
            style={processing ? { ...styles.button, ...styles.payButton, ...styles.disabledButton } : { ...styles.button, ...styles.payButton }}
          >
            {processing ? "Redirecting..." : "Pay with Pesapal"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StallPaymentReview;